/**
 * PlanFeaturesPanel — read-only at-a-glance view of the tenant's plan tier
 * and which features it includes. Two columns: enabled (green dot) and
 * unavailable (muted). Uses /api/tenants/current/plan.
 *
 * Plan is changed by Platform Root admins only via PUT /api/tenants/{id}/plan;
 * this panel intentionally does not surface plan-change controls — that's a
 * future Platform Console screen, not a tenant-Owner self-service flow yet.
 */
import React from 'react';
import { useFeatures } from '../context/FeatureContext';

export default function PlanFeaturesPanel() {
  const {
    ready, planId, planLabel, raw,
    rawPlanId, rawPlanLabel,
    activationStatus, activationLabel,
    effectiveStatus, effectiveStatusLabel,
    trialStartedAt, trialEndsAt, daysRemaining, trialExpired,
  } = useFeatures();

  if (!ready) {
    return (
      <div className="panel" style={{ marginBottom: 16 }} data-testid="plan-features-panel">
        <div className="panel-body">Loading plan…</div>
      </div>
    );
  }
  if (!planId) return null; // failed to load — fail open silently

  const enabled = (raw || []).filter((f) => f.enabled);
  const unavailable = (raw || []).filter((f) => !f.enabled);
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  }) : '—';

  // The activation chip's tint mirrors the trial banner palette so the
  // Settings page reads consistently with the dashboard.
  const statusTint = (() => {
    const s = effectiveStatus || activationStatus;
    if (s === 'trial')      return { bg: '#fef3c7', text: '#92400e' };
    if (s === 'expired')    return { bg: '#f1f5f9', text: '#475569' };
    if (s === 'suspended')  return { bg: '#fee2e2', text: '#991b1b' };
    if (s === 'cancelled')  return { bg: '#fee2e2', text: '#991b1b' };
    return { bg: '#dcfce7', text: '#166534' };
  })();

  const showRawPlanRow = rawPlanId && rawPlanId !== planId;

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="plan-features-panel">
      <div className="panel-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', margin: 0 }}>
            Plan &amp; features
          </h3>
          <span data-testid="plan-features-label" style={{
            fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
            background: '#e0e7ff', color: '#3730a3',
          }}>
            {planLabel || planId}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
          What this tenant's plan includes. The backend enforces every gate — toggling a feature on
          requires changing the plan tier (Platform Root only).
        </p>

        {/* Activation snapshot — only shown when there's something interesting
            to surface (trial, expired, suspended, cancelled, or a raw≠effective
            plan mismatch). Active+matching plans skip this block to keep the
            page restful. */}
        {(activationStatus !== 'active' || showRawPlanRow) && (
          <div data-testid="plan-activation-block" style={{
            marginBottom: 14, padding: '10px 12px', borderRadius: 6,
            background: 'var(--bg-subtle, #f8fafc)',
            border: '1px solid var(--border-subtle, #e5e7eb)',
            display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 12, rowGap: 4,
            fontSize: 12,
          }}>
            <span style={{ color: 'var(--text-secondary)' }}>Activation</span>
            <span data-testid="plan-activation-status">
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
                background: statusTint.bg, color: statusTint.text, marginRight: 6,
              }}>{effectiveStatusLabel || activationLabel || effectiveStatus}</span>
              {activationStatus !== effectiveStatus && (
                <span style={{ color: 'var(--text-secondary)' }}>
                  (raw status: {activationLabel || activationStatus})
                </span>
              )}
            </span>

            {showRawPlanRow && (<>
              <span style={{ color: 'var(--text-secondary)' }}>Raw plan</span>
              <span data-testid="plan-raw-plan">{rawPlanLabel || rawPlanId}</span>
              <span style={{ color: 'var(--text-secondary)' }}>Effective plan</span>
              <span data-testid="plan-effective-plan">{planLabel || planId}</span>
            </>)}

            {(trialStartedAt || trialEndsAt) && (<>
              <span style={{ color: 'var(--text-secondary)' }}>Trial started</span>
              <span data-testid="plan-trial-start">{fmt(trialStartedAt)}</span>
              <span style={{ color: 'var(--text-secondary)' }}>Trial ends</span>
              <span data-testid="plan-trial-end">{fmt(trialEndsAt)}</span>
              {Number.isFinite(daysRemaining) && (<>
                <span style={{ color: 'var(--text-secondary)' }}>Days remaining</span>
                <span data-testid="plan-days-remaining" style={{
                  color: trialExpired ? 'var(--text-secondary)' : (daysRemaining <= 3 ? '#b45309' : 'inherit'),
                }}>{trialExpired ? 'expired' : daysRemaining}</span>
              </>)}
            </>)}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#15803d', marginBottom: 6 }}>
              Enabled ({enabled.length})
            </div>
            {enabled.map((f) => (
              <div key={f.key}
                   data-testid={`plan-feature-enabled-${f.key}`}
                   style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0' }}>
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: '#16a34a' }} />
                <span>{f.label}</span>
                {f.limited && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '0 6px', borderRadius: 4,
                    background: '#fef3c7', color: '#92400e',
                  }}>limited</span>
                )}
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Unavailable ({unavailable.length})
            </div>
            {unavailable.map((f) => (
              <div key={f.key}
                   data-testid={`plan-feature-unavailable-${f.key}`}
                   style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', color: 'var(--text-secondary)' }}>
                <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: '#cbd5e1' }} />
                <span>{f.label}</span>
              </div>
            ))}
            {unavailable.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                Every gated feature is enabled on this plan.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
